import { useEffect, useState, useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend,
} from "recharts";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

export default function TransportadorasVista({ datos }) {
  const [regiones, setRegiones]   = useState([]);
  const [cruce, setCruce]         = useState([]);
  const [regionFiltro, setRegionFiltro] = useState("all");

  useEffect(() => {
    const h = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    fetch(`${API_URL}/api/regiones`,                { headers: h }).then(r => r.json()).then(setRegiones).catch(() => {});
    fetch(`${API_URL}/api/regiones-transportadoras`, { headers: h }).then(r => r.json()).then(setCruce).catch(() => {});
  }, []);

  // Transportadoras únicas del cruce
  const transportadoras = useMemo(() =>
    [...new Set(cruce.map(r => r.transportadora))].sort(), [cruce]);

  // Regiones únicas del cruce
  const regionesUnicas = useMemo(() =>
    [...new Set(cruce.map(r => r.region))].sort(), [cruce]);

  // Datos filtrados por región seleccionada
  const cruceFiltrado = useMemo(() =>
    regionFiltro === "all" ? cruce : cruce.filter(r => r.region === regionFiltro),
  [cruce, regionFiltro]);

  // Tabla pivote: por cada región → columna por transportadora con efectividad
  const pivote = useMemo(() => {
    const mapa = {};
    cruceFiltrado.forEach(r => {
      if (!mapa[r.region]) mapa[r.region] = { region: r.region, _total: 0 };
      mapa[r.region][r.transportadora] = { efectividad: r.efectividad, entregados: r.entregados, fallidos: r.fallidos, total: r.total };
      mapa[r.region]._total += r.total;
    });
    return Object.values(mapa).sort((a, b) => b._total - a._total);
  }, [cruceFiltrado]);

  // Recomendaciones automáticas por región
  const recomendacionesRegion = useMemo(() => {
    const recs = [];
    pivote.forEach(fila => {
      const activas = transportadoras.filter(t => fila[t] && fila[t].total >= 5);
      if (activas.length < 2) return;
      const sorted  = [...activas].sort((a, b) => fila[b].efectividad - fila[a].efectividad);
      const mejor   = sorted[0];
      const segundo = sorted[1];
      const diff    = fila[mejor].efectividad - fila[segundo].efectividad;
      if (diff >= 5) {
        recs.push({
          region:   fila.region,
          mejor,    mejorEf:   fila[mejor].efectividad,
          segundo,  segundoEf: fila[segundo].efectividad,
          diff,
        });
      }
    });
    return recs.sort((a, b) => b.diff - a.diff);
  }, [pivote, transportadoras]);

  if (!Array.isArray(datos) || datos.length === 0) return null;

  const mejor = [...datos].sort((a, b) => b.efectividad - a.efectividad)[0];

  const colorEfect = v => v >= 80 ? "#1D9E75" : v >= 70 ? "#EF9F27" : "#E24B4A";

  return (
    <div>
      {/* ── Resumen transportadoras ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1rem", marginBottom: "1rem" }}>
        {[
          { label: "Transportadoras activas", value: datos.length, color: "#111" },
          { label: "Mejor efectividad",        value: mejor.nombre, color: "#1D9E75", small: true },
          { label: "Efectividad líder",         value: mejor.efectividad?.toFixed(1) + "%", color: "#1D9E75" },
        ].map(k => (
          <div key={k.label} style={{ background: "#f7f7f5", borderRadius: 10, padding: "14px 16px" }}>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>{k.label}</div>
            <div style={{ fontSize: k.small ? 16 : 24, fontWeight: 600, color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* ── Gráfica: Entregados vs Fallidos por transportadora ── */}
      <div style={{ background: "#fff", border: "0.5px solid #e8e8e6", borderRadius: 12, padding: "1rem 1.1rem", marginBottom: "1rem" }}>
        <div style={{ fontSize: 11, fontWeight: 500, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.85rem" }}>
          Entregados vs Fallidos por transportadora
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={datos} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(136,135,128,0.15)" vertical={false} />
            <XAxis dataKey="nombre" tick={{ fontSize: 10, fill: "#888" }} />
            <YAxis tick={{ fontSize: 9, fill: "#888" }} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "0.5px solid #ddd" }} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar dataKey="entregados"  name="Entregados"  fill="#1D9E75" radius={[3, 3, 0, 0]} />
            <Bar dataKey="fallidos"    name="Fallidos"    fill="#E24B4A" radius={[3, 3, 0, 0]} />
            <Bar dataKey="en_transito" name="En tránsito" fill="#534AB7" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* ── Detalle por transportadora ── */}
      <div style={{ background: "#fff", border: "0.5px solid #e8e8e6", borderRadius: 12, padding: "1rem 1.1rem", marginBottom: "1rem" }}>
        <div style={{ fontSize: 11, fontWeight: 500, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "1rem" }}>
          Efectividad por transportadora
        </div>
        {datos.map((t, i) => {
          const color = colorEfect(t.efectividad);
          return (
            <div key={i} style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, display: "inline-block" }} />
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{t.nombre}</span>
                </div>
                <span style={{ fontSize: 13, fontWeight: 600, color }}>{t.efectividad?.toFixed(1)}%</span>
              </div>
              <div style={{ background: "#f5f5f5", borderRadius: 4, height: 7, marginLeft: 16 }}>
                <div style={{ width: `${t.efectividad}%`, height: 7, borderRadius: 4, background: color, transition: "width 0.4s" }} />
              </div>
              <div style={{ fontSize: 11, color: "#888", marginLeft: 16, marginTop: 3 }}>
                Entregados: {t.entregados} &nbsp;·&nbsp; Fallidos: {t.fallidos} &nbsp;·&nbsp; En tránsito: {t.en_transito}
                {t.flete_promedio > 0 && (
                  <span style={{ marginLeft: 8, color: "#555" }}>
                    &nbsp;·&nbsp; Flete prom.:&nbsp;
                    <strong>${Math.round(t.flete_promedio).toLocaleString("es-CL")}</strong>
                  </span>
                )}
              </div>
            </div>
          );
        })}
        {datos.length >= 2 && (
          <div style={{ fontSize: 12, background: "#EAF3DE", color: "#3B6D11", borderRadius: 8, padding: "8px 12px", marginTop: 4 }}>
            <strong>Recomendación:</strong> {mejor.nombre} tiene mayor efectividad. Priorízala para productos de alto margen.
          </div>
        )}
      </div>

      {/* ── Gráfica: Pedidos por región ── */}
      {regiones.length > 0 && (
        <div style={{ background: "#fff", border: "0.5px solid #e8e8e6", borderRadius: 12, padding: "1rem 1.1rem", marginBottom: "1rem" }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.85rem" }}>
            Pedidos por región de Chile
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={regiones} layout="vertical" margin={{ top: 4, right: 50, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(136,135,128,0.15)" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 9, fill: "#888" }} />
              <YAxis type="category" dataKey="region" tick={{ fontSize: 10, fill: "#555" }} width={160} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "0.5px solid #ddd" }} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="entregados"  name="Entregados"  fill="#1D9E75" stackId="a" />
              <Bar dataKey="en_transito" name="En tránsito" fill="#534AB7" stackId="a" />
              <Bar dataKey="fallidos"    name="Fallidos"    fill="#E24B4A" stackId="a" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Efectividad por región × transportadora ── */}
      {pivote.length > 0 && (
        <div style={{ background: "#fff", border: "0.5px solid #e8e8e6", borderRadius: 12, padding: "1rem 1.1rem" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: "0.9rem" }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Efectividad por región × transportadora
            </div>
            {/* Filtro por región */}
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              <button
                onClick={() => setRegionFiltro("all")}
                style={{ fontSize: 10, padding: "3px 10px", borderRadius: 20, cursor: "pointer", border: regionFiltro === "all" ? "none" : "0.5px solid #ddd", background: regionFiltro === "all" ? "#111" : "transparent", color: regionFiltro === "all" ? "#fff" : "#666" }}
              >
                Todas
              </button>
              {regionesUnicas.map(r => (
                <button
                  key={r}
                  onClick={() => setRegionFiltro(r)}
                  style={{ fontSize: 10, padding: "3px 10px", borderRadius: 20, cursor: "pointer", border: regionFiltro === r ? "none" : "0.5px solid #ddd", background: regionFiltro === r ? "#111" : "transparent", color: regionFiltro === r ? "#fff" : "#666" }}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          {/* Hint de scroll en mobile */}
          <div style={{ fontSize: 10, color: "#ccc", marginBottom: 6, display: "flex", justifyContent: "flex-end" }}>
            ← desliza para ver todo →
          </div>

          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            {/* Cabecera dinámica */}
            <div style={{ display: "grid", gridTemplateColumns: `minmax(110px,2fr) repeat(${transportadoras.length}, minmax(80px,1fr))`, borderBottom: "0.5px solid #eee", minWidth: 400 }}>
              <div style={{ fontSize: 10, color: "#aaa", padding: "4px 6px", fontWeight: 500 }}>Región</div>
              {transportadoras.map(t => (
                <div key={t} style={{ fontSize: 10, color: "#aaa", padding: "4px 6px", fontWeight: 500, textAlign: "center",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 100 }}>{t}</div>
              ))}
            </div>

            {/* Filas */}
            {pivote.map((fila, i) => (
              <div
                key={fila.region}
                style={{ display: "grid", gridTemplateColumns: `minmax(110px,2fr) repeat(${transportadoras.length}, minmax(80px,1fr))`,
                  borderBottom: i < pivote.length - 1 ? "0.5px solid #f5f5f4" : "none",
                  background: i % 2 === 0 ? "transparent" : "#fafaf9", minWidth: 400 }}
              >
                <div style={{ fontSize: 11, padding: "7px 6px", color: "#444", fontWeight: 500,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                  title={fila.region}>{fila.region}</div>
                {transportadoras.map(t => {
                  const d = fila[t];
                  if (!d) return <div key={t} style={{ fontSize: 11, padding: "7px 4px", color: "#ddd", textAlign: "center" }}>—</div>;
                  const c = colorEfect(d.efectividad);
                  return (
                    <div key={t} style={{ padding: "5px 4px", textAlign: "center" }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: c }}>{d.efectividad.toFixed(1)}%</div>
                      <div style={{ fontSize: 9, color: "#bbb" }}>{d.entregados}✓ {d.fallidos}✗</div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <div style={{ fontSize: 10, color: "#bbb", marginTop: 8 }}>
            ✓ entregados &nbsp;·&nbsp; ✗ fallidos &nbsp;·&nbsp; — sin operaciones en esa zona
          </div>

          {/* Recomendaciones por región */}
          {recomendacionesRegion.length > 0 && (
            <div style={{ marginTop: "1rem", borderTop: "0.5px solid #f0f0ee", paddingTop: "1rem" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#555", textTransform: "uppercase",
                letterSpacing: "0.05em", marginBottom: "0.65rem" }}>
                Recomendaciones por región
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {recomendacionesRegion.map((r, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8,
                    background: "#f4fbf8", border: "0.5px solid #1D9E7530", borderRadius: 8,
                    padding: "8px 12px", fontSize: 12 }}>
                    <span style={{ fontSize: 14, lineHeight: 1 }}>💡</span>
                    <span style={{ color: "#333", lineHeight: 1.5 }}>
                      En <strong>{r.region}</strong>,{" "}
                      <span style={{ color: "#1D9E75", fontWeight: 600 }}>{r.mejor}</span>{" "}
                      ({r.mejorEf.toFixed(1)}%) supera a{" "}
                      <span style={{ color: "#E24B4A" }}>{r.segundo}</span>{" "}
                      ({r.segundoEf.toFixed(1)}%) con{" "}
                      <strong>{r.diff.toFixed(1)} puntos</strong> de ventaja — considera priorizar{" "}
                      {r.mejor} para esa zona.
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
